/**
 * Localization entity class
 */

/**
 * LocalizationFactory
 * returns Localization entity class
 *
 * @class LocalizationFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Localization} class
 *
 * @ngdoc factory
 * @name mwApp.factory:LocalizationFactory
 */

import { has } from '../../../app/helpers/object'
/* @ngInject */
export default function LocalizationFactory ($http, alertLiteSvc, Entity, m2mMixin, onOffMixin, upDownMixin) {
  const escapeDictionaryValue = (value) => {
    if (typeof value !== 'string') return value
    return value
      .replace(/\r\n/g, '\\r\\n')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
  }

  const escapeDictionary = (dictionary) => {
    if (!dictionary || typeof dictionary !== 'object') return dictionary
    const escaped = {}
    Object.keys(dictionary).forEach((k) => {
      escaped[k] = escapeDictionaryValue(dictionary[k])
    })
    return escaped
  }

  const unescapeDictionaryValue = (value) => {
    if (typeof value !== 'string') return value
    return value
      .replace(/\\r\\n/g, '\r\n')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
  }

  const normalizeDictionary = (dictionary) => {
    if (!dictionary || typeof dictionary !== 'object') return dictionary
    const normalized = {}
    Object.keys(dictionary).forEach((k) => {
      normalized[k] = unescapeDictionaryValue(dictionary[k])
    })
    return normalized
  }

  /* === Localization class === */

  /**
   * Localization model
   *
   * @class Localization
   * @classdesc Localization model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @return {models.Localization} Localization entity
   */
  function Localization () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }

  Localization.prototype = Object.create(Entity.prototype)
  Localization.prototype.constructor = Localization

  /* === Localization instance methods & fields === */

  Object.assign(Localization.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Localization#
     * @type {Object}
     */
    _fields: {
      appName: String,
      isDefault: false,
      isEnabled: false,
      lastUpdatedBy: String,
      lastUpdatedTime: String,
      networkId: Number,
      localization: {
        dictionary: {},
        locale: {
          tag: String,
          languageName: String
        }
      }
    },

    /**
     * url template
     *
     * @memberof models.Localization#
     * @type {String}
     */
    _urlTemplate: 'api/i18n/applications/{{::appName}}/localizations/{{::localization.locale.tag}}',

    /**
     * Get URL for localization
     *
     * @memberof models.Localization#
     * @return {String} URL
     */
    _getUrl: function () {
      if (this.networkId) {
        return `api/networks/${this.networkId}/i18n/applications/${this.appName}/localizations/${this.localization.locale.tag}`
      }
      return this.$_urlTemplate(this)
    },

    /**
     * public url template
     *
     * @memberof models.Localization#
     * @type {String}
     */
    _publicUrlTemplate: 'app-localization/{{::locale}}',

    /**
     * Set Localization as default (or not default), performing PATCH request to the server
     *
     * @memberof models.Localization#
     * @return {Promise} promise
     */
    _default: function () {
      const url = this._getUrl()
      return $http.patch(url, '"MAKE_DEFAULT"')
        .then(response => {
          this._weakCopy(response.data)
          return response
        })
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.Localization
     * @return {Promise} promise
     */
    _onOff: function () {
      const url = this._getUrl()
      return $http.patch(url, this.isEnabled ? '"ENABLE"' : '"DISABLE"')
        .catch(rejection => {
          this.isEnabled = !this.isEnabled
          return rejection
        })
    },

    /**
     * Delete entity, performing DELETE request to backend
     * Override to support networkId in URL
     *
     * @memberof models.Localization#
     * @return {Promise} promise
     */
    _delete: function () {
      const url = this._getUrl()
      return $http.delete(url)
    }

  }, m2mMixin('networks'), upDownMixin('sortOrder'))

  /* === Localization static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Localization, Entity)
  Localization._name = 'Localization'
  Localization.init()

  /**
   * Load all localizations
   * Override to support networkId in URL
   *
   * @memberof models.Localization
   * @param  {String|Object} url  - entity url string or object with appName and optional networkId
   * @param  {String|Object} cast - structure, will be encapsulated in array
   * @return {Promise} promise
   */
  Localization.loadAll = function (url, cast) {
    let actualUrl = url
    let networkId = null
    if (typeof url === 'object') {
      if (url.networkId) {
        networkId = url.networkId
        actualUrl = `api/networks/${url.networkId}/i18n/applications/${url.appName}/localizations`
      } else if (url.appName) {
        actualUrl = `api/i18n/applications/${url.appName}/localizations`
      }
    }
    return Entity.loadAll.call(this, actualUrl, cast)
      .then(data => {
        if (networkId && Array.isArray(data)) {
          data.forEach(loc => {
            loc.networkId = networkId
          })
        }
        return data
      })
  }

  // permissions
  Localization.access = {
    view: 'viewLocalization',
    create: 'createLocalization',
    edit: 'editLocalization',
    delete: 'deleteLocalization'
  }

  /**
   * Load all translations
   *
   * @memberof models.Localization
   * @param  {String|Object} o - scope for {@link models.Entity#_urlTemplate}
   * @return {Promise} promise
   */
  Localization.loadLocale = function (o) {
    const url = o.networkId
      ? `api/networks/${o.networkId}/i18n/applications/${o.appName}/localizations/${o.localization.locale.tag}`
      : this.prototype.$_urlTemplate(o)
    return $http.get(url).then(res => {
      const loc = new Localization(res.data?.payload)
      if (loc?.localization?.dictionary) {
        loc.localization.dictionary = escapeDictionary(loc.localization.dictionary)
      }
      if (o.networkId) {
        loc.networkId = o.networkId
      }
      return loc
    })
  }

  /**
   * Load available languages
   *
   * @memberof models.Localization
   * @param {String} app - application name
   * @param {Number} [networkId] - network ID (optional)
   * @return {Promise} promise
   */

  Localization.getAvailableLanguages = function (app, networkId) {
    const url = 'api/' +
      (networkId ? `networks/${networkId}/` : '') +
      `i18n/applications/${app}/locales/free`
    return $http.get(url)
      .then(response => has('payload')(response.data) ? response.data.payload : response.data)
      .catch(alertLiteSvc.alertError)
  }
  /**
   * Update localization
   *
   * @memberof models.Localization
   * @param  {Object} locale - scope for {@link models.Entity#_urlTemplate}
   * @return {Promise} promise
   */
  Localization.update = function (locale) {
    const url = locale._getUrl && typeof locale._getUrl === 'function'
      ? locale._getUrl()
      : 'api/' + (locale.networkId ? `networks/${locale.networkId}/` : '') +
        `i18n/applications/${locale.appName}/localizations/${locale.localization.locale.tag}`
    return $http.patch(`${url}/dictionary`, normalizeDictionary(locale.localization.dictionary))
  }

  /**
   * Create localization
   *
   * @memberof models.Localization
   * @param  {Object} locale - scope for {@link models.Entity#_urlTemplate}
   * @return {Promise} promise
   */
  Localization.save = function (locale) {
    const url = 'api/' +
      (locale.networkId ? `networks/${locale.networkId}/` : '') +
      `i18n/applications/${locale.appName}/localizations`

    return $http.post(url, {
      localeTag: locale.localization.locale.tag,
      dictionary: normalizeDictionary(locale.localization.dictionary),
      enabled: true
    })
  }

  /**
   * Get glossary
   *
   * @memberof models.Localization
   * @param {String} app - application name
   * @return {Promise} promise
   */
  Localization.getGlossary = function (app) {
    return $http.get(`api/i18n/applications/${app}/glossary`)
      .then(response => has('payload')(response.data) ? response.data.payload : response.data)
      .catch(alertLiteSvc.alertError)
  }

  return Localization
}
